/**
 * LobbyVision 2 adapter CDO editor form model — template + expansion-toggles
 * redesign (see `~/development/claude_plans/lobbyvision2_template_toggle_redesign.md`).
 *
 * This is a UI-facing model aligned to the generated `Config.LobbyVision2Template`
 * FlatBuffer Object API (`flatbuffers/generated/config/lobby-vision2-template.ts`).
 * It is converted to/from `Config.LobbyVision2TemplateT` by
 * `flatbuffers/builders/lobbyVision2CdoBuilder.ts` — like OPC UA and Modbus,
 * this CDO cannot go through the JSON `cdo_input` path, so it is packed to
 * FlatBuffer binary and sent via `cdo_binary` (create) or
 * `PUT /adapters/:id/cdo` (update).
 *
 * The adapter is bank/lift-agnostic: a template entry carries the authored
 * telegraph op fields (frame type / operation id / operation type / data)
 * plus an LN2 message and the four LN1-style expansion toggles
 * (unitsBased/floorsBased/bankLevel/individual). There is NO telegraph
 * bank_num/lift_num and NO LN2 bank_id/lift_id on the adapter — LiftCloud
 * expands the template per car/floor at CDO-generation time (mirroring
 * `ExpandModbusTemplateFromFB`), deriving `lift_num`/`data` from the
 * per-adapter `addressing` config and stamping `bank_num` from the assigned
 * bank's `logical_port`.
 *
 * `message` on each row is `Ln2MappingData` (see
 * `pages/components/adapters/Ln2MappingEditor.tsx`) but the builder only
 * reads its `message` (LN2 content) field — `bank_id`/`lift_id` are ignored
 * (the editor renders with `hideBankLift`), since the template never
 * authors LN2 bank/lift identity.
 */

import { FrameType } from "../flatbuffers/generated/lobby-vision2/frame-type";
import { OperationType } from "../flatbuffers/generated/lobby-vision2/operation-type";
import { SecureCarCalltype } from "../flatbuffers/generated/lobby-vision2/secure-car-calltype";
import { MessageContent } from "../flatbuffers/generated/ln2/message-content";
import { Traffic } from "../flatbuffers/generated/traffic/traffic";
import { Control } from "../flatbuffers/generated/control/control";
import { LobbyVision2LiftNumSource as FbLobbyVision2LiftNumSource } from "../flatbuffers/generated/config/lobby-vision2-lift-num-source";
import type { FbMessageRef } from "./FbMessage.interface";
import type { Ln2MappingData } from "../pages/components/adapters/ln2MappingData";

export type { FrameType, OperationType };

/**
 * The `OperationType` arms that represent a call telegraph — a Car Call
 * (`carcall`), an allocated car call (`alloc_cc`), or a Secure Car Call
 * (`secure_cc`). When a mapping row authors one of these, the LV2 wire needs
 * to know WHICH call it is (front/rear car call, or up/down front/rear hall
 * call). That sub-classification is the generated `SecureCarCalltype` enum,
 * which rides in the dedicated `ccType` field — so for these operations
 * `ccType` is required, independent of `data` (which stays a plain byte).
 */
const LOBBYVISION2_CALL_OPERATION_TYPES: readonly OperationType[] = [
  OperationType.carcall,
  OperationType.alloc_cc,
  OperationType.secure_cc,
];

/** True when the operation is a Car Call / allocated car call / Secure Car Call (needs a `SecureCarCalltype` in `ccType`). */
export function isLobbyVision2CallOperation(operationType: OperationType): boolean {
  return LOBBYVISION2_CALL_OPERATION_TYPES.includes(operationType);
}

/** Numeric `SecureCarCalltype` arms (1..6). Derived from the generated enum so a schema change is picked up automatically. */
const SECURE_CAR_CALLTYPE_VALUES: readonly number[] = Object.values(SecureCarCalltype).filter(
  (v): v is number => typeof v === "number",
);

/**
 * True when a value is a valid `SecureCarCalltype` arm (1..6). `0` means "not
 * yet chosen". Used to validate `ccType` (a `Config.CallType` value) — the
 * numeric arms of `CallType` and `SecureCarCalltype` are identical, so this
 * validator is reused as-is rather than duplicated.
 */
export function isValidSecureCarCalltype(value: number): boolean {
  return SECURE_CAR_CALLTYPE_VALUES.includes(value);
}

/**
 * `Traffic` message values that represent PLACING a car/hall call (front/rear
 * car call, up/down front/rear hall call). Excludes the Assigned/Answered
 * variants — those report a call's lifecycle, they do not place one.
 */
const TRAFFIC_CALL_MESSAGE_VALUES: readonly number[] = [
  Traffic.CarCall,
  Traffic.RearCarCall,
  Traffic.HallCall,
  Traffic.UpHallCall,
  Traffic.UpRearHallCall,
  Traffic.DownHallCall,
  Traffic.DownRearHallCall,
];

/**
 * `Control` message values that energize (place) or secure a car/hall call.
 * Excludes Lock/Unlock, Bypass, and Disable variants — those gate or mask a
 * call rather than placing/securing one.
 */
const CONTROL_CALL_MESSAGE_VALUES: readonly number[] = [
  Control.EnergizeCarCall,
  Control.EnergizeRearCarCall,
  Control.EnergizeUpHallCall,
  Control.EnergizeUpRearHallCall,
  Control.EnergizeDownHallCall,
  Control.EnergizeDownRearHallCall,
  Control.SecureCarCall,
  Control.SecureRearCarCall,
];

/**
 * True when the mapped LN2 message is a Car Call / Hall Call / Secure Car Call
 * placement message (a `Traffic` or `Control` call from the sets above). This
 * is the message-side trigger for the required `SecureCarCalltype` (in
 * `ccType`): a mapping that carries such a message must tell the LV2 wire
 * which physical call it is, regardless of the vendor `operationType`.
 */
export function isLobbyVision2CallLn2Message(ref: FbMessageRef | null): boolean {
  if (!ref) return false;
  if (ref.messageContent === MessageContent.Traffic_TrafficMessage) {
    return TRAFFIC_CALL_MESSAGE_VALUES.includes(ref.messageValue);
  }
  if (ref.messageContent === MessageContent.Control_ControlMessage) {
    return CONTROL_CALL_MESSAGE_VALUES.includes(ref.messageValue);
  }
  return false;
}

/**
 * True when a row needs a `SecureCarCalltype` in `ccType`: EITHER the LV2
 * telegraph `operationType` is a call op, OR the mapped LN2 message is a call
 * placement message. Both triggers are honored so the field surfaces whether
 * the user starts from the vendor op or from the LN2 message.
 */
export function lobbyVision2RowNeedsCallType(row: {
  operationType: OperationType;
  message: Ln2MappingData;
}): boolean {
  return (
    isLobbyVision2CallOperation(row.operationType) ||
    isLobbyVision2CallLn2Message(row.message.message)
  );
}

/**
 * True when a row's call-type coupling is satisfied. Rows that don't need a
 * call type (see `lobbyVision2RowNeedsCallType`) are always OK. A row that
 * does must carry a valid `SecureCarCalltype` value in `ccType`.
 *
 * `ccType` is a dedicated field for the call type (see `LobbyVision2ReadRow.
 * ccType`) — it no longer shares a slot with `data`, so a call mapping and
 * `floorsBased` (per-floor `data` derivation) are no longer mutually
 * exclusive. Works for both read and write rows (both expose these fields).
 */
export function lobbyVision2RowCallTypeValid(row: {
  operationType: OperationType;
  ccType: number;
  message: Ln2MappingData;
}): boolean {
  if (!lobbyVision2RowNeedsCallType(row)) return true;
  return isValidSecureCarCalltype(row.ccType);
}

/**
 * Derive the `ccType` value to keep when a row's call-mode changes. `ccType`
 * only has meaning in call-mode — a chosen `SecureCarCalltype` — so when
 * call-mode flips (via a changed `operationType` OR a changed LN2 message)
 * the stale value is reset to `0`, forcing an explicit re-pick. When
 * call-mode is unchanged the existing value is preserved. `data` is a plain,
 * always-editable byte (floor or raw data, like Modbus/OPC-UA) and is
 * unaffected by call-mode changes — it is never reset here.
 */
export function ccTypeForCallModeChange(
  wasCallMode: boolean,
  isCallMode: boolean,
  ccType: number,
): number {
  return wasCallMode === isCallMode ? ccType : 0;
}

/** UI-facing mirror of `Config.LobbyVision2LiftNumSource`. */
export type LobbyVision2LiftNumSource = "index" | "liftPosition" | "carNum";

/** Convert the UI string union to the generated FlatBuffer enum value. */
export function liftNumSourceToFb(source: LobbyVision2LiftNumSource): FbLobbyVision2LiftNumSource {
  if (source === "liftPosition") return FbLobbyVision2LiftNumSource.LiftPosition;
  if (source === "carNum") return FbLobbyVision2LiftNumSource.LiftCarNum;
  return FbLobbyVision2LiftNumSource.IndexBased;
}

/** Convert the generated FlatBuffer enum value back to the UI string union. */
export function liftNumSourceFromFb(
  source: FbLobbyVision2LiftNumSource,
): LobbyVision2LiftNumSource {
  if (source === FbLobbyVision2LiftNumSource.LiftPosition) return "liftPosition";
  if (source === FbLobbyVision2LiftNumSource.LiftCarNum) return "carNum";
  return "index";
}

/**
 * Per-adapter addressing config (mirrors `Config.LobbyVision2AddressingConfig`).
 * One scheme governs the whole adapter — LV2 wire addressing is a property
 * of the vendor controller/protocol, not of an individual message (mirrors
 * `ModbusTemplate.registerOffsets`/slave-id living once on the template).
 */
export interface LobbyVision2Addressing {
  /** Selects the units_based lift_num derivation. Default "index". */
  liftNumSource: LobbyVision2LiftNumSource;
  /** Base added to car_index when liftNumSource = "index". Default 1. */
  liftNumBase: number;
  /** Sentinel lift_num emitted for bank_level entries. Default 0. */
  bankLiftNum: number;
  /** Floor->data derivation base. Default 0. */
  floorDataBase: number;
  /** Floor->data derivation step. Default 1. */
  floorDataStep: number;
  /** When true, add Cdo.vendor_floor_offset to the floor data. Default false. */
  applyVendorFloorOffset: boolean;
}

/** Default addressing scheme: classic 1-based index addressing (reproduces today's implied behavior). */
export const BLANK_LOBBYVISION2_ADDRESSING: LobbyVision2Addressing = {
  liftNumSource: "index",
  liftNumBase: 1,
  bankLiftNum: 0,
  floorDataBase: 0,
  floorDataStep: 1,
  applyVendorFloorOffset: false,
};

/**
 * Config-time LV2 inbound mapping: telegraph(response) → LN2 message.
 *
 * Bank/lift-agnostic. Telegraph `bankNum`/`liftNum` are NOT stored here —
 * they are DERIVED at expansion time from the bank's `logical_port` plus
 * per-car index (governed by the adapter's `addressing` config).
 */
export interface LobbyVision2ReadRow {
  /** Response arm (secureCCResponse | statusResponse) — authored VERBATIM. */
  telegraphType: FrameType;
  /** VERBATIM — not expanded. */
  operationId: number;
  /** VERBATIM. */
  operationType: OperationType;
  /**
   * Plain data byte (floor number when `floorsBased`, otherwise a free raw
   * byte) — always editable, like Modbus/OPC-UA's ordinary data byte.
   * Independent of call type: see `ccType` for that.
   */
  data: number;
  /**
   * LN2 content only — an `Ln2MappingData` rendered with `hideBankLift`.
   * The message picker yields a composite key; `bank_id`/`lift_id` fields
   * on the row are ignored by the builder.
   */
  message: Ln2MappingData;
  /** Duplicate per lift at expansion time. */
  unitsBased: boolean;
  /** Duplicate per floor at expansion time. */
  floorsBased: boolean;
  /** Target the whole bank (lift_id = bank_id, lift_num = addressing.bankLiftNum). Wins over individual. */
  bankLevel: boolean;
  /** Expand on its own addressing lane rather than sharing the adapter default. */
  individual: boolean;
  /**
   * Per-row lift_num source, used ONLY when `individual = true` — overrides
   * the adapter-level `addressing.liftNumSource` for this row's units_based
   * expansion. Ignored when `individual = false`. Default "index".
   */
  liftNumSourceOverride: LobbyVision2LiftNumSource;
  /**
   * Per-row base added to car_index when `liftNumSourceOverride = "index"`
   * (only meaningful when `individual = true`). Mirrors
   * `addressing.liftNumBase`. Default 1.
   */
  liftNumBaseOverride: number;
  label: string | null;
  /** Optional inverse message (mirrors Modbus's pairedLn2Message). null = unpaired. */
  pairedMessage: Ln2MappingData | null;
  /**
   * Secure car call type — see `SecureCarCalltypeSelect`; carries call type
   * separately from `data` so `data` is free for floor.
   */
  ccType: number;
  /** Allocated-call direction — round-tripped for wire completeness, not yet surfaced in the UI. */
  allocType: number;
}

/**
 * Config-time LV2 outbound mapping: LN2 message → telegraph(request).
 */
export interface LobbyVision2WriteRow {
  /** Request arm (statusRequest | changeRequest | commandRequest) — authored VERBATIM. */
  telegraphType: FrameType;
  operationId: number;
  operationType: OperationType;
  /**
   * Plain data byte (floor number when `floorsBased`, otherwise a free raw
   * byte) — always editable, like Modbus/OPC-UA's ordinary data byte.
   * Independent of call type: see `ccType` for that.
   */
  data: number;
  message: Ln2MappingData;
  unitsBased: boolean;
  floorsBased: boolean;
  bankLevel: boolean;
  individual: boolean;
  /** Per-row lift_num source override — see `LobbyVision2ReadRow.liftNumSourceOverride`. */
  liftNumSourceOverride: LobbyVision2LiftNumSource;
  /** Per-row lift_num base override — see `LobbyVision2ReadRow.liftNumBaseOverride`. */
  liftNumBaseOverride: number;
  label: string | null;
  /**
   * Secure car call type — see `SecureCarCalltypeSelect`; carries call type
   * separately from `data` so `data` is free for floor.
   */
  ccType: number;
  /** Allocated-call direction — round-tripped for wire completeness, not yet surfaced in the UI. */
  allocType: number;
}

export interface AdapterCdoLobbyVision2 {
  /** Telegraph-response → LN2 (was msgToLn2Map). */
  reads: LobbyVision2ReadRow[];
  /** LN2 → telegraph-request (was ln2ToMsgMap). */
  writes: LobbyVision2WriteRow[];
  /** Per-adapter addressing scheme; defaults reproduce 1-based index addressing. */
  addressing: LobbyVision2Addressing;
}

/** A blank read row with sane defaults — response side defaults to statusResponse. */
export function blankLobbyVision2ReadRow(message: Ln2MappingData): LobbyVision2ReadRow {
  return {
    telegraphType: FrameType.statusResponse,
    operationId: 0,
    operationType: OperationType.status,
    data: 0,
    message,
    unitsBased: false,
    floorsBased: false,
    bankLevel: false,
    individual: false,
    liftNumSourceOverride: "index",
    liftNumBaseOverride: 1,
    label: null,
    pairedMessage: null,
    ccType: 0,
    allocType: 0,
  };
}

/** A blank write row with sane defaults — request side defaults to statusRequest. */
export function blankLobbyVision2WriteRow(message: Ln2MappingData): LobbyVision2WriteRow {
  return {
    telegraphType: FrameType.statusRequest,
    operationId: 0,
    operationType: OperationType.status,
    data: 0,
    message,
    unitsBased: false,
    floorsBased: false,
    bankLevel: false,
    individual: false,
    liftNumSourceOverride: "index",
    liftNumBaseOverride: 1,
    label: null,
    ccType: 0,
    allocType: 0,
  };
}
